// Client de la API pública de `messages-business`: el historial de mensajes del cliente. El
// openapi no tipa el cuerpo, así que la forma sale del DTO de @fiado/type-kit.
import { MessageMomentCategoryEnum } from '@fiado/type-kit/bin/messageMoments/enums/MessageMomentCategoryEnum';
import type { CustomerMessageHistoryResponse } from '@fiado/type-kit/bin/customerMessages/dtos/responses/CustomerMessageHistoryResponse';
import { apiFetch } from '../../client';
import type { operations } from './messages-business.gen';

export type { CustomerMessageHistoryResponse };
export type { CustomerMessageHistoryItem } from '@fiado/type-kit/bin/customerMessages/dtos/responses/CustomerMessageHistoryItem';

export type CustomerMessageHistoryPageQuery = NonNullable<
    operations['backofficeListCustomerSaleMessages']['parameters']['query']
>;

/** El servicio de mensajes no tiene base URL en este ambiente. */
export class MessagesServiceNotConfiguredError extends Error {
    constructor() {
        super('El servicio de mensajes no está configurado en este ambiente.');
        this.name = 'MessagesServiceNotConfiguredError';
    }
}

/** Base URL leída en cada llamada; `null` si falta o viene vacía. */
function configuredBaseUrl(): string | null {
    const value = import.meta.env.VITE_API_MESSAGES_BUSINESS_BASE_URL as string | undefined;
    return value?.trim() ? value : null;
}

const PATH_BY_CATEGORY: Record<MessageMomentCategoryEnum, string> = {
    [MessageMomentCategoryEnum.SALE]: 'sale',
    [MessageMomentCategoryEnum.CREDIT]: 'credit',
};

/**
 * `backofficeListCustomerSaleMessages` / `backofficeListCustomerCreditMessages` — UNA página del
 * historial, del más nuevo al más viejo. Permiso `messages.history.<sale|credit>.read`.
 */
export function listCustomerMessages(
    retailCustomerId: string,
    category: MessageMomentCategoryEnum,
    query: CustomerMessageHistoryPageQuery = {},
): Promise<CustomerMessageHistoryResponse> {
    const baseUrl = configuredBaseUrl();
    if (!baseUrl) return Promise.reject(new MessagesServiceNotConfiguredError());
    const search = new URLSearchParams();
    if (query.cursor !== undefined) search.set('cursor', query.cursor);
    if (query.limit !== undefined) search.set('limit', String(query.limit));
    const rendered = search.toString();
    const path = `/customers/${encodeURIComponent(retailCustomerId)}/messages/${PATH_BY_CATEGORY[category]}`;
    return apiFetch<CustomerMessageHistoryResponse>(rendered ? `${path}?${rendered}` : path, {
        baseUrl,
    });
}
